interface PreloadConnectedThirdPartyConfigParams {
	cloudId?: string;
	userId?: string | null;
}

/**
 * Fire-and-forget warm-up of the third-party connector-config cache that
 * `useConnectedThirdPartyProductKeys` reads at editor mount (for the
 * `confluence_quick_insert_embeds` experiment).
 *
 * Warming it during the edit-route preload — before `FullPageEditorComponent`
 * mounts — keeps the request off the editor's hydration critical path. The
 * quick-insert embed items (Google Drive, Figma, …) then resolve from cache
 * almost immediately instead of appearing only after a mid-load fetch, and the
 * network + JSON parsing no longer competes with hydration in the treatment
 * cohort.
 *
 * `get3pProductKeys` is a plain (non-hook) cache-backed request keyed on
 * `userId` + `cloudId`, so this populates the exact same cache entry the editor
 * hook reads (both use the default endpoint). It never throws and never blocks
 * the transition; if the warm-up misses, the editor-mount hook still fetches.
 *
 * Client-only: the underlying request uses a localStorage-backed cache, so
 * callers must not invoke this during SSR.
 */
export const preloadConnectedThirdPartyConfig = ({
	cloudId,
	userId,
}: PreloadConnectedThirdPartyConfigParams): void => {
	if (!cloudId || !userId) {
		return;
	}

	void import(
		/* webpackChunkName: "loadable-rovo-connectors-config-hooks" */
		'@atlassian/rovo-connectors-config/hooks'
	)
		.then(({ get3pProductKeys }) => get3pProductKeys({ cloudId, userId }))
		.catch(() => {
			// Best-effort warm-up only. Any failure is swallowed so it can never
			// affect the route transition; the editor-mount hook will re-fetch.
		});
};
